import { useQueryState } from 'nuqs';
import {
  Banner,
  Grid,
  Page,
  PageBody,
  PageHeader,
  PageHeaderDescription,
  PageHeaderTitle,
  Stack,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useWorkspaceApiCredentials } from '@/features/integrations';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import { useAnonymousEventsMergeSetting } from '@/pages/workspaces/settings/merge-options/hooks';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { trackAssetHost } from '@/utils/data-center';
import { getCurrentRegion } from '@/utils/environment';
import { CredentialsPanel } from './components/credentials-panel';
import { IdentifyPeoplePanel } from './components/identify-people-panel';
import { JavascriptSnippetPanel } from './components/javascript-snippet-panel';
import {
  useApiCredentialsHref,
  useCustomerioBreadcrumbs,
  useMergeOptionsHref,
  useWorkspaceSettingsHref,
} from './hooks';
import { buildCustomerioPageModel, type CustomerioPageModel } from './page-model';

const PAGE_TITLE = 'Customer.io API';

/**
 * The `/integrations/customerio` page: the workspace's tracking credentials and
 * the JavaScript install guide built from them.
 *
 * Read-only. The one write the page describes — the anonymous-events merge
 * setting — lives on the merge-options settings page, which this page links to.
 */
export default function CustomerioConnectorPage() {
  const workspace = useCurrentWorkspace();
  const credentials = useWorkspaceApiCredentials();

  // Ember's route blocked its template on the credentials, and the snippet is built from one of
  // them, so the page waits rather than rendering an empty credentials panel that fills in later.
  //
  // `ConnectorRouteView` holds a loader until the workspace resolves, so by here there is one — the
  // null branch is what narrows the type, not a state this reaches.
  if (workspace == null || credentials.isPending) {
    return <LoadingPage />;
  }

  return (
    <>
      <DocumentTitle title={PAGE_TITLE} />
      <Page>
        <PageHeader>
          <PageHeaderTitle>{PAGE_TITLE}</PageHeaderTitle>
          <PageHeaderDescription>Integrate with Customer.io using our API.</PageHeaderDescription>
        </PageHeader>
        <PageBody>
          {credentials.isError ? (
            <Banner
              variant="error"
              title="We couldn't load this workspace's API credentials."
              description="Refresh the page to try again."
            />
          ) : (
            <CustomerioConnectorContent
              model={buildCustomerioPageModel(workspace)}
              credentials={credentials.data ?? []}
            />
          )}
        </PageBody>
      </Page>
    </>
  );
}

type CustomerioConnectorContentProps = {
  model: CustomerioPageModel;
  credentials: readonly ApiCredential[];
};

function CustomerioConnectorContent({ model, credentials }: CustomerioConnectorContentProps) {
  const { term } = useProductTaxonomy();
  const region = getCurrentRegion();
  const mergeSetting = useAnonymousEventsMergeSetting(Number(model.workspaceId));

  // The activity-log empty state deep-links here with `?fragment=snippet` to put the reader on the
  // tracking snippet.
  const [fragment] = useQueryState('fragment');

  useCustomerioBreadcrumbs(model.workspaceId);
  const apiCredentialsHref = useApiCredentialsHref(model.workspaceId);
  const mergeOptionsHref = useMergeOptionsHref(model.workspaceId);
  const workspaceSettingsHref = useWorkspaceSettingsHref(model.workspaceId);

  return (
    <Grid columns="8fr 4fr" gap="200">
      <Stack gap="200">
        <CredentialsPanel
          credentials={credentials}
          workspaceName={model.workspaceName}
          region={region}
          apiCredentialsHref={apiCredentialsHref}
        />
        <JavascriptSnippetPanel
          credentials={credentials}
          assetHost={trackAssetHost(region)}
          mergeOptionsHref={mergeOptionsHref}
          isAnonymousActivityMerged={mergeSetting.enabled}
          hasMergeSettingLoaded={mergeSetting.hasLoaded}
          shouldScrollIntoView={fragment === 'snippet'}
          identify={model.identify}
        />
      </Stack>
      <IdentifyPeoplePanel
        allEnabledIdentifiers={model.allEnabledIdentifiers}
        enabledIdentifierCount={model.enabledIdentifierCount}
        isMultiIdentifier={model.isMultiIdentifier}
        workspaceSettingsHref={workspaceSettingsHref}
        peoplePlural={term('people.plural')}
        peopleSingular={term('people.singular')}
      />
    </Grid>
  );
}
